import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import SDK from 'utils/sdk';
import {
  networkPerformanceDownloadReport,
  networkPerformanceGetDashboard,
} from './actions';
import {
  getNetworkPerformanceDateRange,
  getNetworkPerformanceGranularity,
} from './selectors';

// openapi-client-axios hands axios `params` with array values (e.g.
// locationIds), and the default serializer comma-joins them into a single
// value. Serialize arrays as repeated params (`locationIds=a&locationIds=b`)
// so the backend receives each id as a separate array entry.
const serializeParams = (params) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((item) => search.append(key, item));
    } else {
      search.append(key, value);
    }
  });
  return search.toString();
};

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  (typeof error === 'string' ? error : error?.message) ||
  fallback;

// When a request uses `responseType: 'blob'`, both error bodies and non-PDF
// success bodies arrive as a Blob, so the server's JSON message can't be read
// off `error.response.data` directly. Read the blob text and pull out the
// message. Returns null when there's nothing usable to surface.
const extractBlobMessage = async (blob) => {
  if (!blob || typeof blob.text !== 'function') return null;
  try {
    const text = await blob.text();
    if (!text) return null;
    try {
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.error || null;
    } catch {
      // Non-JSON body (plain text or an HTML error/redirect page). Only use
      // short bodies so we don't dump a full HTML page into a toast.
      return text.length < 200 ? text : null;
    }
  } catch {
    return null;
  }
};

function* getDashboardSaga() {
  try {
    const { startDate, endDate } = yield select(getNetworkPerformanceDateRange);
    if (!startDate || !endDate) {
      yield put(networkPerformanceGetDashboard.fulfill());
      return;
    }
    const granularity = yield select(getNetworkPerformanceGranularity);
    yield put(networkPerformanceGetDashboard.request());
    const client = yield call([SDK, 'getClient']);
    const { data } = yield call([client, 'getNetworkDashboard'], {
      startDate: format(new Date(startDate), 'yyyy-MM-dd'),
      endDate: format(new Date(endDate), 'yyyy-MM-dd'),
      // Bucket the response by whole months or whole Sun–Sat weeks.
      granularity,
    });
    yield put(networkPerformanceGetDashboard.success(data));
  } catch (error) {
    yield put(networkPerformanceGetDashboard.failure(error));
    toast.error(
      getErrorMessage(error, 'Failed to load the network performance dashboard')
    );
  } finally {
    yield put(networkPerformanceGetDashboard.fulfill());
  }
}

function* downloadReportSaga({ payload }) {
  try {
    const { studioIds = [], dateRange, granularity = 'month' } = payload || {};
    const { startDate, endDate } = dateRange || {};

    if (!startDate || !endDate) {
      yield put(networkPerformanceDownloadReport.fulfill());
      return;
    }
    yield put(networkPerformanceDownloadReport.request());
    const client = yield call([SDK, 'getClient']);
    const formattedStart = format(new Date(startDate), 'yyyy-MM-dd');
    const formattedEnd = format(new Date(endDate), 'yyyy-MM-dd');
    const response = yield call(
      [client, 'getNetworkPerformanceReportPdf'],
      {
        startDate: formattedStart,
        endDate: formattedEnd,
        // Pass the selected studios so the report matches the dashboard view.
        locationIds: studioIds,
        granularity,
      },
      {},
      { responseType: 'blob', paramsSerializer: serializeParams }
    );
    // A blob response can still be an error the server returned with a 2xx
    // status (e.g. a JSON error envelope or an empty body). Downloading that
    // as a `.pdf` would produce a file the user can't open, with no error
    // shown — so verify it's actually a non-empty PDF before saving it.
    const contentType =
      response?.headers?.['content-type'] || response?.data?.type || '';
    if (!contentType.includes('application/pdf') || !response.data?.size) {
      const message = yield call(extractBlobMessage, response.data);
      throw new Error(message || 'The report service did not return a PDF');
    }
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);
    try {
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `network-performance-${formattedStart}_${formattedEnd}.pdf`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
    } finally {
      window.URL.revokeObjectURL(url);
    }
    yield put(networkPerformanceDownloadReport.success());
  } catch (error) {
    yield put(networkPerformanceDownloadReport.failure(error));
    // On a failed blob request the server's message lives inside the Blob
    // body rather than on `error.response.data`, so read it out first and
    // only fall back to the generic message when nothing usable is found.
    const blobData = error?.response?.data;
    const blobMessage =
      blobData instanceof Blob
        ? yield call(extractBlobMessage, blobData)
        : null;
    toast.error(
      blobMessage || getErrorMessage(error, 'Failed to download the PDF report')
    );
  } finally {
    yield put(networkPerformanceDownloadReport.fulfill());
  }
}

export default function* networkPerformanceWatcher() {
  yield all([
    takeLatest(networkPerformanceGetDashboard.TRIGGER, getDashboardSaga),
    takeLatest(networkPerformanceDownloadReport.TRIGGER, downloadReportSaga),
  ]);
}
